<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import SettingsHeader from 'dashboard/routes/dashboard/settings/SettingsHeader.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import DddAssignmentsPanel from './DddAssignmentsPanel.vue';
import CompanyAssignmentsPanel from './CompanyAssignmentsPanel.vue';
import ContactAssignmentsPanel from './ContactAssignmentsPanel.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { isCloudFeatureEnabled } = useAccount();

const isCompaniesEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.COMPANIES)
);

const tabs = computed(() => [
  { key: 'ddd', label: t('ASSIGNMENT_RULES.TABS.DDD') },
  ...(isCompaniesEnabled.value
    ? [{ key: 'company', label: t('ASSIGNMENT_RULES.TABS.COMPANY') }]
    : []),
  { key: 'contact', label: t('ASSIGNMENT_RULES.TABS.CONTACT') },
]);

const activeTab = computed(() => {
  const tab = tabs.value.find(({ key }) => key === route.query.tab);
  return tab ? tab.key : 'ddd';
});
const activeTabIndex = computed(() =>
  tabs.value.findIndex(tab => tab.key === activeTab.value)
);

const backUrl = computed(
  () => `/app/accounts/${route.params.accountId}/contacts`
);

const onTabChanged = tab => {
  router.replace({ query: { ...route.query, tab: tab.key } });
};
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-surface-1">
    <SettingsHeader
      :header-title="t('ASSIGNMENT_RULES.TITLE')"
      show-back-button
      :back-button-label="t('CONTACT_PROFILE.BACK_BUTTON')"
      :back-url="backUrl"
      :show-new-button="false"
    />
    <div class="flex-1 overflow-auto">
      <div class="px-4 pt-4">
        <p class="mb-3 text-sm text-n-slate-11">
          {{ t('ASSIGNMENT_RULES.DESCRIPTION') }}
        </p>
        <TabBar
          :tabs="tabs"
          :initial-active-tab="activeTabIndex"
          @tab-changed="onTabChanged"
        />
      </div>
      <CompanyAssignmentsPanel v-if="activeTab === 'company'" />
      <ContactAssignmentsPanel v-else-if="activeTab === 'contact'" />
      <DddAssignmentsPanel v-else />
    </div>
  </div>
</template>
